import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { validationSchema } from "../../../../service/validation/Assign Surveyor";
import { ApiService } from "../../../../service/api.service";
import { MASTER_URL, OPERTIONS_URL } from "../../../../common/common";

// Components
import Input from "../../../../components/FormInputs";
import CustomDropDown from "../../../../components/Dropdown/CustomDropDown";
import DatePicker from "../../../../components/Datepicker";
import SecureImage from "../../../../components/BucketImage/Image";
import { assignsurveyor } from "../../../../common/svg";
import Managesurveyor from "../ManageSurveyor";

interface AssignsurveyorProps {
  isOpen: boolean;
  onClose: () => void;
  tripID?: string;
  bookingID?: string;
}

const Assignsurveyor: React.FC<AssignsurveyorProps> = ({
  isOpen,
  onClose,
  tripID,
  bookingID,
}) => {
  interface AssignSurveyorFormValues {
    surveyorId: string;
    surveyorName?: string;
    liner: string;
    startDate: string;
    endDate: string;
    isImmediate?: boolean;
  }

  const {
    register,
    handleSubmit,
    formState: { errors },
    trigger,
    reset,
    getValues,
    setValue,
  } = useForm<AssignSurveyorFormValues>({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      surveyorId: "",
      surveyorName: "",
      liner: "",
      startDate: "",
      endDate: "",
      isImmediate: false,
    },
  });

  const [surveyors, setSurveyors] = useState<any[]>([]);
  const [selectedSurveyor, setSelectedSurveyor] = useState<string | number>("");
  const [selectedSurveyorDetails, setSelectedSurveyorDetails] = useState<any>(null);
  const [assignedSurveyors, setAssignedSurveyors] = useState<any[]>([]);
  const [isSurveyorFormOpen, setIsSurveyorFormOpen] = useState(false);
  const apiService = new ApiService();

  const fetchSurveyors = async () => {
    try {
      // Correctly access response.body.data
      const response: any = await apiService.getApi(`${MASTER_URL}/surveyor/get-route`);
      console.log("Surveyors API response:", response);

      if (response.status === "200") {
        const surveyorsData = response.body.data.map((surveyor: any) => ({
          label: surveyor.firstName + " " + surveyor.lastName,
          value: surveyor.surveyorId,
          ...surveyor // Store full surveyor data
        }));
        setSurveyors(surveyorsData);
        console.log("Processed surveyors data:", surveyorsData);
      } else {
        console.error("Invalid response format from surveyors API:", response);
        setSurveyors([]);
      }
    } catch (error) {
      console.error("Error fetching surveyors:", error);
      setSurveyors([]);
    }
  };

  const handleSurveyorSelection = (surveyorId: string | number) => {
    const surveyorIdStr = String(surveyorId);
    setSelectedSurveyor(surveyorIdStr);
    setValue("surveyorId", surveyorIdStr);

    // Find and set the selected surveyor details for immediate display
    const selectedSurveyorData = surveyors.find(s => s.value === surveyorIdStr);
    if (selectedSurveyorData) {
      setSelectedSurveyorDetails(selectedSurveyorData);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchSurveyors();
    }
  }, [isOpen]);

  const addSurveyorData = async () => {
    // Trigger validation
    const isValid = await trigger(["liner", "startDate", "endDate"]);

    if (!isValid) {
      console.warn("Form validation failed");
      return;
    }

    const formData = getValues() as {
      surveyorId: string;
      surveyorName?: string;
      liner?: string;
      startDate?: string;
      endDate?: string;
      isImmediate?: boolean;
      notes?: string;
    };

    if (!formData.surveyorId) {
      console.warn("No surveyor selected");
      return;
    }

    // Find the selected surveyor to get the name
    const selectedSurveyorData = surveyors.find(s => s.value === formData.surveyorId);

    const dataToStore = {
      surveyorId: formData.surveyorId,
      surveyorName: selectedSurveyorData?.label || "",
      liner: formData.liner || "",
      startDate: formData.startDate || "",
      endDate: formData.endDate || "",
      isImmediate: formData.isImmediate || false,
      bookingId: bookingID,
      tripId: tripID,
    };

    console.log("Adding Surveyor Assignment:", dataToStore);
    setAssignedSurveyors((prev) => [...prev, dataToStore]);

    // Reset form fields for next entry
    reset({
      surveyorId: "",
      liner: "",
      startDate: "",
      endDate: "",
      isImmediate: false,
    });
    setSelectedSurveyor("");
    setSelectedSurveyorDetails(null);
  };

  const getSurveyorLabel = (surveyorId: string) => {
    const found = surveyors.find(s => s.value === surveyorId);
    return found ? found.label : surveyorId;
  };

  const removeSurveyor = (index: number) => {
    setAssignedSurveyors(prev => prev.filter((_, i) => i !== index));
  };

  const onSubmit = async () => {
    try {
      const payload = {
        surveyorDetails: assignedSurveyors
      };

      const response = await apiService.postApi(`${OPERTIONS_URL}/surveyor/store-route`, payload);

      if (response && response.status === "200") {
        console.log("Surveyor assigned successfully:", response);
        reset();
        setAssignedSurveyors([]);
        onClose();
      } else {
        console.error("Failed to assign surveyor:", response);
      }
    } catch (error) {
      console.error("Error submitting form:", error);
    }
  };

  const handleCancel = () => {
    reset();
    setAssignedSurveyors([]);
    onClose();
  };

  const handleNewSurveyor = () => {
    setIsSurveyorFormOpen(true);
  };

  const handleSurveyorFormClose = () => {
    setIsSurveyorFormOpen(false);
  };

  const handleSurveyorAdded = () => {
    // Refresh surveyors list after adding new surveyor
    fetchSurveyors();
    setIsSurveyorFormOpen(false);
  };

  // Reusable function to reset the form
  const resetForm = () => {
    setSelectedSurveyor("");
    setSelectedSurveyorDetails(null);
    setValue("surveyorId", "");
    setValue("liner", "");
    setValue("startDate", "");
    setValue("endDate", "");
    setValue("isImmediate", false);
    reset();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-30 z-50">
      <div className="bg-white rounded-lg shadow-lg w-[800px] max-h-[80vh] overflow-auto flex flex-col">
        {/* Modal Header */}
        <div className="flex justify-between items-center p-3 border-b">
          <h2 className="text-[16px] font-[600] font-poppins text-[#444FBD]">
            Assign Surveyor
          </h2>
          <button onClick={onClose} className="text-gray-600 hover:text-black">
            ✕
          </button>
        </div>

        {/* Modal Body */}
        <div className="flex-grow p-3 flex">
          {/* Left Side - Form */}
          <div className="w-1/2 pr-3">
            <div className="flex justify-end mb-0.5">
              <button
                type="button"
                className="text-[#444FBD] text-[12px] font-[400] font-poppins bg-transparent border-none cursor-pointer flex items-center gap-1"
                onClick={addSurveyorData}
              >
                Add more Surveyor
              </button>
            </div>

            {/* Dropdown */}
            <div className="ml-4">
              <CustomDropDown
                register={register}
                name="surveyorId"
                errors={errors}
                options={surveyors || []}
                label="Surveyor Name"
                onChange={handleSurveyorSelection}
              />
              <div className="flex justify-end mt-0.5">
                <button
                  type="button"
                  className="text-[#444FBD] text-[12px] font-[400] font-poppins bg-transparent border-none cursor-pointer"
                  onClick={handleNewSurveyor}
                >
                  Add New Surveyor
                </button>
              </div>
            </div>

            {/* Liner Input */}
            <div className="flex flex-col mt-3 mb-2 ml-4">
              <Input
                label="Liner"
                name="liner"
                errors={errors}
                register={register}
                placeholder="Enter Liner"
                onBlur={() => trigger("liner")}
              />
            </div>


            {/* Date Pickers */}
            <div className="mt-3 ml-4">
              <DatePicker
                label="Starting Date & Time"
                onDateChange={(date: any) => setValue("startDate", date)}
                register={register("startDate")}
                name="startDate"
                errors={errors}
                allowPastDates={true}
                includeTime={true}
                disabled={false}
              />
            </div>

            <div className="flex items-center justify-end mt-2">
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  {...register("isImmediate")}
                  className="w-[12px] h-[12px]"
                />
                <span className="text-[#282828] text-[12px] font-normal">
                  Immediate
                </span>
              </label>
            </div>

            <div className="mt-3 ml-4">
              <DatePicker
                label="End Date & Time"
                onDateChange={(date: any) => setValue("endDate", date)}
                register={register("endDate")}
                name="endDate"
                errors={errors}
                allowPastDates={true}
                includeTime={true}
                disabled={false}
              />
            </div>

          </div>

          {/* Right Side - Surveyor Display or Illustration */}
          <div className="w-1/2 ml-3 flex flex-col border-[#C1C1C1] border-l border-dashed pl-3">
            {assignedSurveyors.length > 0 ? (
              <div className="w-full">
                <h3 className="text-[14px] font-[500] text-[#444FBD] mb-3">Surveyor Details</h3>
                <div className="space-y-3">
                  {assignedSurveyors.map((surveyor, index) => (
                    <div key={index} className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm relative">
                      {/* Remove button at top right of card */}
                      <button
                        type="button"
                        onClick={() => {
                          removeSurveyor(index);
                          resetForm();
                        }}
                        className="absolute top-2 right-2 w-5 h-5 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center text-xs font-bold transition-colors"
                        title="Remove surveyor and reset form"
                      >
                        ✕
                      </button>

                      <div className="flex items-start space-x-3">
                        {/* Avatar */}
                        <div className="relative">
                          <SecureImage
                            fileKey={assignsurveyor}
                            alt="Surveyor"
                            width="80px"
                            className="w-20 h-20 rounded-lg object-cover"
                          />
                          {/* Green dot at top */}
                          <div className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white"></div>
                          {/* Available badge */}
                          <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 px-3 py-1 bg-green-100 text-green-800 text-[8px] rounded-full border border-green-300">
                            Available
                          </div>
                        </div>

                        {/* Surveyor Info */}
                        <div className="flex-1">

                          <div className="space-y-1 text-[10px]">
                            <div className="flex items-center">
                              <span className="text-gray-700 font-medium">Name :</span>
                              <span className="text-blue-600 font-medium ml-1">
                                {surveyor.surveyorName || getSurveyorLabel(surveyor.surveyorId)}
                              </span>
                            </div>

                            <div className="flex items-center">
                              <span className="text-gray-700 font-medium">Company :</span>
                              <span className="text-blue-600 ml-1">Vendor Name</span>
                            </div>

                            <div className="flex items-center">
                              <span className="text-gray-700 font-medium">Mobile No :</span>
                              <span className="text-gray-600 ml-1">67875864658</span>
                            </div>

                            <div className="flex items-center">
                              <span className="text-gray-700 font-medium">Email Id :</span>
                              <span className="text-gray-600 ml-1">ebjcsjcbkvjd</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : selectedSurveyorDetails ? (
              <div className="w-full">
                <h3 className="text-[14px] font-[500] text-[#444FBD] mb-3">Surveyor Details</h3>
                <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm relative">
                  {/* Remove button at top right of card */}
                  <button
                    type="button"
                    onClick={resetForm}
                    className="absolute top-2 right-2 w-5 h-5 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center text-xs font-bold transition-colors"
                    title="Remove surveyor and reset form"
                  >
                    ✕
                  </button>

                  <div className="flex items-start space-x-3">
                    {/* Avatar */}
                    <div className="relative">
                      <SecureImage
                        fileKey={assignsurveyor}
                        alt="Surveyor"
                        width="80px"
                        className="w-20 h-20 rounded-lg object-cover"
                      />
                      {/* Green dot at top */}
                      <div className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white"></div>
                      {/* Available badge */}
                      <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 px-3 py-1 bg-green-100 text-green-800 text-[8px] rounded-full border border-green-300">
                        Available
                      </div>
                    </div>

                    {/* Surveyor Info */}
                    <div className="flex-1">

                      <div className="space-y-1 text-[10px]">
                        <div className="flex items-center">
                          <span className="text-gray-700 font-medium">Name :</span>
                          <span className="text-blue-600 font-medium ml-1">
                            {selectedSurveyorDetails.label}
                          </span>
                        </div>

                        <div className="flex items-center">
                          <span className="text-gray-700 font-medium">Company :</span>
                          <span className="text-blue-600 ml-1">Vendor Name</span>
                        </div>

                        <div className="flex items-center">
                          <span className="text-gray-700 font-medium">Mobile No :</span>
                          <span className="text-gray-600 ml-1">67875864658</span>
                        </div>

                        <div className="flex items-center">
                          <span className="text-gray-700 font-medium">Email Id :</span>
                          <span className="text-gray-600 ml-1">ebjcsjcbkvjd</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-full">
                <SecureImage
                  fileKey={assignsurveyor}
                  alt="Assign Surveyor"
                  width="200px"
                />
                <p className="text-xs text-gray-500 text-center mt-1">
                  Please assign Surveyor for the task
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Modal Footer */}
        <div className="flex gap-3 justify-end p-2 border-t">
          <button
            type="button"
            onClick={handleCancel}
            className="w-28 h-8 rounded-lg bg-white text-gray-800 border border-gray-300 hover:border-gray-400 transition duration-200 text-sm"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onSubmit}
            className="w-28 h-8 rounded-lg bg-[#444FBD] text-white transition duration-200 hover:bg-opacity-90 text-sm"
            disabled={assignedSurveyors.length === 0}
          >
            Save
          </button>
        </div>
      </div>

      {/* Surveyor Form Modal - Higher z-index to appear above Assign Surveyor modal */}
      {isSurveyorFormOpen && (
        <div className="fixed inset-0 z-[60]">
          <Managesurveyor
            open={isSurveyorFormOpen}
            setOpen={handleSurveyorFormClose}
            onSuccess={handleSurveyorAdded}
          />
        </div>
      )}
    </div>
  );
};

export default Assignsurveyor;